import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { STRIPE_PRODUCTS, formatPrice } from '../stripe-config';
import { createCheckoutSession, getUserSubscription, isSubscriptionActive } from '../lib/stripe';
import { useAuth } from '../contexts/AuthContext';
import { CheckCircle, X, Loader2, Users, Zap, Shield, Brain, Target } from 'lucide-react';

const Pricing: React.FC = () => {
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [currentSubscription, setCurrentSubscription] = useState<any>(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const navigate = useNavigate();
  const { userProfile } = useAuth();

  useEffect(() => {
    const fetchSubscription = async () => {
      if (!userProfile) return;
      
      try {
        setSubscriptionLoading(true);
        const data = await getUserSubscription();
        setCurrentSubscription(data);
      } catch (err) {
        console.error('Error fetching subscription:', err);
      } finally {
        setSubscriptionLoading(false);
      }
    };

    fetchSubscription();
  }, [userProfile]);

  const handleSubscribe = async (priceId: string) => {
    try {
      setLoading(priceId);
      setError(null);

      if (!userProfile) {
        navigate('/login?redirect=/pricing');
        return;
      }

      const { url } = await createCheckoutSession(
        priceId,
        `${window.location.origin}/success?session_id={CHECKOUT_SESSION_ID}`,
        `${window.location.origin}/pricing`
      );

      if (url) {
        window.location.href = url;
      } else {
        throw new Error('Failed to create checkout session');
      }
    } catch (err: any) {
      console.error('Error creating checkout session:', err);
      setError(err.message || 'Failed to process subscription');
    } finally {
      setLoading(null);
    }
  };

  const isCurrentPlan = (priceId: string) => {
    return currentSubscription?.price_id === priceId && 
           isSubscriptionActive(currentSubscription?.subscription_status);
  };

  const features = {
    'ADAPT personal': [
      { text: 'Strategic Dashboard', icon: Target },
      { text: 'Experiment Hub', icon: Zap },
      { text: 'Prioritised Backlog', icon: CheckCircle },
      { text: 'Market Insights', icon: Brain },
      { text: 'AI Co-Pilot (Limited)', icon: Brain },
      { text: 'Single User Only', icon: Users }
    ],
    'ADAPT start-up': [
      { text: 'All Personal Features', icon: CheckCircle },
      { text: 'Full AI Co-Pilot Access', icon: Brain },
      { text: 'Role-Specific Dashboards', icon: Target },
      { text: 'Team Collaboration', icon: Users },
      { text: 'Up to 20 Users', icon: Users },
      { text: 'Priority Support', icon: Shield }
    ],
    'ADAPT Enterprise': [
      { text: 'All Start-up Features', icon: CheckCircle },
      { text: 'Unlimited Users', icon: Users },
      { text: 'Advanced Analytics', icon: Target },
      { text: 'Custom Integrations', icon: Zap },
      { text: 'Dedicated Account Manager', icon: Users },
      { text: 'Enterprise SLAs', icon: Shield }
    ]
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4">Choose Your ADAPT Plan</h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Select the plan that best fits your needs and start making data-driven decisions with our AI-powered framework.
          </p>
        </div>

        {error && (
          <div className="max-w-md mx-auto mb-8 bg-red-900 border border-red-700 rounded-lg p-4 flex items-center">
            <X className="w-5 h-5 text-red-400 mr-2 flex-shrink-0" />
            <span className="text-red-300">{error}</span>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {STRIPE_PRODUCTS.map((product) => {
            const isCurrentSub = isCurrentPlan(product.priceId);
            
            return (
              <div 
                key={product.id} 
                className={`bg-gray-800 rounded-xl overflow-hidden border shadow-lg transition-all hover:shadow-xl ${
                  isCurrentSub 
                    ? 'border-indigo-500 ring-2 ring-indigo-500 ring-opacity-50' 
                    : 'border-gray-700 hover:border-gray-600'
                }`}
              >
                <div className="p-6 border-b border-gray-700">
                  <h2 className="text-2xl font-bold mb-2">{product.name}</h2>
                  <p className="text-gray-400 mb-4">{product.description}</p>
                  <div className="flex items-baseline">
                    <span className="text-4xl font-bold">{formatPrice(product.price)}</span>
                    {product.mode === 'subscription' && (
                      <span className="text-gray-400 ml-2">/month</span>
                    )}
                  </div>
                </div>

                <div className="p-6">
                  <ul className="space-y-4 mb-8">
                    {features[product.name as keyof typeof features]?.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <feature.icon className="w-5 h-5 text-indigo-400 mr-2 flex-shrink-0 mt-0.5" />
                        <span>{feature.text}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => handleSubscribe(product.priceId)}
                    disabled={loading === product.priceId || isCurrentSub || subscriptionLoading}
                    className={`w-full py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center ${
                      isCurrentSub
                        ? 'bg-green-600 text-white cursor-default'
                        : 'bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-70 disabled:cursor-not-allowed'
                    }`}
                  >
                    {loading === product.priceId ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : isCurrentSub ? (
                      <>
                        <CheckCircle className="w-5 h-5 mr-2" />
                        Current Plan
                      </>
                    ) : subscriptionLoading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      <>Subscribe</>
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-16 bg-gray-800 rounded-xl p-8 border border-gray-700">
          <h2 className="text-2xl font-bold mb-6 text-center">Frequently Asked Questions</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">What's included in the free plan?</h3>
              <p className="text-gray-400">
                The ADAPT personal plan includes access to our core features including the Strategic Dashboard, 
                Experiment Hub, Prioritised Backlog, and Market Insights. You'll also get limited access to our 
                AI Co-Pilot.
              </p>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">Can I upgrade or downgrade my plan?</h3>
              <p className="text-gray-400">
                Yes, you can upgrade or downgrade your plan at any time. Changes will take effect at the end of your 
                current billing cycle.
              </p>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">How does user counting work?</h3>
              <p className="text-gray-400">
                For the Start-up plan, you can have up to 20 users. The Enterprise plan supports unlimited users. 
                Users are counted as active accounts with access to your organization's ADAPT instance.
              </p>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">Do you offer custom enterprise solutions?</h3>
              <p className="text-gray-400">
                Yes, for larger organizations with specific needs, we offer custom enterprise solutions with 
                dedicated support, custom integrations, and tailored features. Contact our sales team for more information.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-12 text-center">
          <p className="text-gray-400">
            All plans include a 14-day free trial. No credit card required to start.
          </p>
          <p className="text-gray-500 mt-2">
            By subscribing, you agree to our Terms of Service and Privacy Policy.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Pricing;